import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
} from "react";

import {
  DataTable,
  TableContainer,
  TableToolbar,
  TableToolbarContent,
  TableToolbarSearch,
  Table,
  TableHead,
  TableRow,
  TableHeader,
  TableBody,
  TableCell,
  Pagination,
  InlineLoading,
  InlineNotification,
  Tag,
  Grid,
  Column,
  DatePicker,
  DatePickerInput,
  Dropdown,
  Button,
} from "@carbon/react";

import { Filter, Document } from "@carbon/icons-react";
import { callApi } from "../../../services/apiService";

const headers = [
  { key: "invoiceNumber", header: "Invoice Number" },
  { key: "createdAt", header: "Created At" },
  { key: "billingName", header: "Billing Name" },
  { key: "billingEmail", header: "Billing Email" },
  //{ key: "transactionId", header: "Transaction ID" },
  { key: "totalAmount", header: "Total Amount" },
  { key: "status", header: "Status" },
  { key: "pdf", header: "PDF" },
];

// ------------------ Currency ------------------

const formatCurrency = (amount, currency = "INR") => {
  if (amount === null || amount === undefined || amount === "") {
    return `${currency} 0.00`;
  }

  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(amount));
};

// ------------------ Date ------------------

const formatDisplayDate = (dateValue) => {
  if (!dateValue) return "N/A";

  const date = new Date(dateValue);

  if (Number.isNaN(date.getTime())) {
    return dateValue;
  }

  return date.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
};

// ------------------ Status ------------------

const getStatusTagType = (status) => {
  switch (status?.toLowerCase()) {
    case "paid":
      return "green";

    case "pending":
      return "yellow";

    case "cancelled":
    case "canceled":
      return "red";

    case "overdue":
      return "red";

    case "draft":
      return "gray";

    default:
      return "blue";
  }
};

// ------------------ Valid Carbon Tag Types ------------------

const VALID_TAG_TYPES = [
  "red",
  "magenta",
  "purple",
  "blue",
  "cyan",
  "teal",
  "green",
  "gray",
  "cool-gray",
  "warm-gray",
  "high-contrast",
  "outline",
];

const getValidTagType = (type) => {
  return VALID_TAG_TYPES.includes(type) ? type : "gray";
};

// ======================================================
// Invoice Report
// ======================================================

export default function InvoiceReport() {
  const mountedRef = useRef(true);

  const [rows, setRows] = useState([]);

  // ------------------ Loading / Error ------------------

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // ------------------ Pagination / Search ------------------

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);

  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  // ------------------ Filters ------------------

  const [statusFilter, setStatusFilter] = useState(null);
  const [statusOptions, setStatusOptions] = useState([]);

  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  // ======================================================
  // Search debounce
  // ======================================================

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm.trim().toLowerCase());
    }, 350);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // ======================================================
  // Normalize Invoice
  // ======================================================

  const normalizeInvoiceData = useCallback((item, index) => {
    return {
      id: String(
        item.invoice_id ||
          item.invoice_number ||
          `${item.transaction_id || "invoice"}-${index}`
      ),

      invoiceId: item.invoice_id,

      invoiceNumber: item.invoice_number,

      createdAt: item.created_at,

      billingName: item.billing_name,

      billingEmail: item.billing_email,

      //transactionId: item.transaction_id,

      totalAmount: item.total_amount || 0,

      currency: item.currency || "INR",

      status: item.status,

      pdfPath: item.pdf_path,

      paymentId: item.payment_id,

      propertyUuid: item.property_uuid,

      invoiceDate: item.invoice_date,

      dueDate: item.due_date,

      items: item.items || [],

      originalData: item,
    };
  }, []);

  // ======================================================
  // Fetch Invoice Report
  // ======================================================

  const fetchInvoiceReport = useCallback(async () => {
    if (!mountedRef.current) return;

    setLoading(true);
    setError("");

    try {
      const response = await callApi(
        "GET",
        "/payments/invoices/reports"
      );

      console.log("Invoice Report API:", response);
      console.log("Invoices:", response?.data?.invoices);

      if (
        response?.status_code === 200 &&
        response?.data
      ) {
        const invoices = response.data.invoices || [];

        const normalizedData = invoices.map(
          normalizeInvoiceData
        );

        setRows(normalizedData);

        // Dynamic status options
        const statuses = [
          ...new Set(
            invoices
              .map((item) => item.status)
              .filter(Boolean)
          ),
        ];

        const statusOpts = statuses.map((status) => ({
          id: status,
          text: status,
        }));

        setStatusOptions(statusOpts);
      } else {
        throw new Error("Invalid response format");
      }
    } catch (error) {
      console.error("Invoice report error:", error);

      setError(
        "Failed to load invoice report. Please try again."
      );

      setRows([]);
    } finally {
      if (mountedRef.current) {
        setLoading(false);
      }
    }
  }, [normalizeInvoiceData]);

  // ======================================================
  // Initial Load
  // ======================================================

  useEffect(() => {
    mountedRef.current = true;

    fetchInvoiceReport();

    return () => {
      mountedRef.current = false;
    };
  }, [fetchInvoiceReport]);

  // ======================================================
  // Format API date
  // ======================================================

  const formatApiDate = (date) => {
    if (!date) return "";

    const year = date.getFullYear();

    const month = String(
      date.getMonth() + 1
    ).padStart(2, "0");

    const day = String(
      date.getDate()
    ).padStart(2, "0");

    return `${year}-${month}-${day}`;
  };

  // ======================================================
  // Apply Filters
  // ======================================================

  const applyFilters = async () => {
    if (!mountedRef.current) return;

    setLoading(true);
    setError("");

    try {
      const queryParams = new URLSearchParams();

      if (statusFilter?.id) {
        queryParams.append(
          "status",
          statusFilter.id
        );
      }

      if (startDate) {
        queryParams.append(
          "start_date",
          formatApiDate(startDate)
        );
      }

      if (endDate) {
        queryParams.append(
          "end_date",
          formatApiDate(endDate)
        );
      }

      if (debouncedSearch) {
        queryParams.append(
          "search",
          debouncedSearch
        );
      }

      const queryString =
        queryParams.toString();

      const endpoint = queryString
        ? `/payments/invoices/reports?${queryString}`
        : "/payments/invoices/reports";

      console.log("Invoice Report URL:", endpoint);

      const response = await callApi(
        "GET",
        endpoint
      );

      if (
        response?.status_code === 200 &&
        response?.data
      ) {
        const invoices =
          response.data.invoices || [];

        const normalizedData =
          invoices.map(
            normalizeInvoiceData
          );

        setRows(normalizedData);
        setPage(1);
      } else {
        setRows([]);
      }
    } catch (error) {
      console.error(
        "Invoice filter error:",
        error
      );

      setError(
        "Failed to load filtered invoices."
      );

      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  // ======================================================
  // Reset Filters
  // ======================================================

  const resetFilters = () => {
    setStatusFilter(null);

    setStartDate(null);
    setEndDate(null);

    setSearchTerm("");
    setDebouncedSearch("");

    setPage(1);

    fetchInvoiceReport();
  };

  // ======================================================
  // Search
  // ======================================================

  const filteredRows = useMemo(() => {
    if (!debouncedSearch) {
      return rows;
    }

    const search =
      debouncedSearch.toLowerCase();

    return rows.filter((row) => {
      return (
        row.invoiceNumber
          ?.toLowerCase()
          .includes(search) ||

        row.billingName
          ?.toLowerCase()
          .includes(search) ||

        row.billingEmail
          ?.toLowerCase()
          .includes(search) ||

        String(row.transactionId || "")
          .toLowerCase()
          .includes(search) ||

        row.status
          ?.toLowerCase()
          .includes(search)
      );
    });
  }, [rows, debouncedSearch]);

  // ======================================================
  // Pagination
  // ======================================================

  const paginatedRows = useMemo(
    () =>
      filteredRows.slice(
        (page - 1) * pageSize,
        page * pageSize
      ),
    [filteredRows, page, pageSize]
  );

  useEffect(() => {
    setPage(1);
  }, [rows]);

  // ======================================================
  // Active Filters
  // ======================================================

  const activeFilterCount = [
    statusFilter,
    startDate,
    endDate,
    debouncedSearch,
  ].filter(Boolean).length;

  // ======================================================
  // PDF
  // ======================================================

  const handleViewPdf = (row) => {
    if (!row.pdfPath) {
      setError(
        "PDF is not available for this invoice."
      );
      return;
    }

    console.log(
      "Invoice PDF Path:",
      row.pdfPath
    );

    /*
      IMPORTANT:
      pdfPath currently comes from API as:

      invoices/invoice_1.pdf

      If your backend provides a PDF endpoint,
      replace the URL below with that endpoint.

      Example:

      const pdfUrl =
        `${API_BASE_URL}/payments/invoices/${row.invoiceId}/pdf`;
    */

    const pdfUrl = row.pdfPath;

    window.open(
      pdfUrl,
      "_blank",
      "noopener,noreferrer"
    );
  };

  // ======================================================
  // Render
  // ======================================================

  return (
    <>
      {/* ==================================================
          Filter Section
      ================================================== */}

      <div
        style={{
          padding: "1rem",
          backgroundColor: "#f4f4f4",
          borderRadius: "8px",
          marginBottom: "1rem",
          border: "1px solid #e0e0e0",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            marginBottom: "1rem",
          }}
        >
          <Filter
            style={{
              marginRight: "0.5rem",
            }}
          />

          <h4
            style={{
              margin: 0,
              flex: 1,
            }}
          >
            Filter Invoices
          </h4>
        </div>

        <Grid style={{ padding: 0 }}>
          {/* STATUS */}

          <Column lg={4} md={4} sm={4}>
            <Dropdown
              id="invoice-status-filter"
              items={statusOptions}
              titleText="Invoice Status"
              label="Choose"
              itemToString={(item) =>
                item?.text || ""
              }
              selectedItem={statusFilter}
              onChange={({
                selectedItem,
              }) =>
                setStatusFilter(
                  selectedItem
                )
              }
              size="sm"
            />
          </Column>

          {/* START DATE */}

          <Column lg={4} md={4} sm={4}>
            <DatePicker
              datePickerType="single"
              value={startDate}
              onChange={(dates) =>
                setStartDate(
                  dates[0]
                )
              }
            >
              <DatePickerInput
                id="invoice-start-date"
                labelText="Start Date"
                placeholder="dd/mm/yyyy"
                size="sm"
              />
            </DatePicker>
          </Column>

          {/* END DATE */}

          <Column lg={4} md={4} sm={4}>
            <DatePicker
              datePickerType="single"
              value={endDate}
              onChange={(dates) =>
                setEndDate(
                  dates[0]
                )
              }
            >
              <DatePickerInput
                id="invoice-end-date"
                labelText="End Date"
                placeholder="dd/mm/yyyy"
                size="sm"
              />
            </DatePicker>
          </Column>
        </Grid>

        {/* Buttons */}

        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            marginTop: "1rem",
            gap: "0.75rem",
          }}
        >
          <Button
            kind="secondary"
            onClick={resetFilters}
          >
            Reset
          </Button>

          <Button
            onClick={applyFilters}
          >
            Search
          </Button>
        </div>

        {/* Filter Tags */}

        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: "0.5rem",
            marginTop: "1rem",
          }}
        >
          {statusFilter && (
            <Tag
              type="blue"
              filter
              onClose={() =>
                setStatusFilter(null)
              }
            >
              Status:{" "}
              {statusFilter.text}
            </Tag>
          )}

          {startDate && (
            <Tag
              type="teal"
              filter
              onClose={() =>
                setStartDate(null)
              }
            >
              Start:{" "}
              {new Date(
                startDate
              ).toLocaleDateString()}
            </Tag>
          )}

          {endDate && (
            <Tag
              type="teal"
              filter
              onClose={() =>
                setEndDate(null)
              }
            >
              End:{" "}
              {new Date(
                endDate
              ).toLocaleDateString()}
            </Tag>
          )}

          {searchTerm && (
            <Tag
              type="magenta"
              filter
              onClose={() => {
                setSearchTerm("");
                setDebouncedSearch("");
              }}
            >
              Search:{" "}
              {searchTerm}
            </Tag>
          )}
        </div>
      </div>

      {/* ==================================================
          Invoice Table
      ================================================== */}

      <DataTable
        key={JSON.stringify(
          paginatedRows.map(
            (r) => r.id
          )
        )}
        rows={paginatedRows}
        headers={headers}
      >
        {({
          rows,
          headers,
          getHeaderProps,
          getRowProps,
          getTableProps,
        }) => {
          return (
            <TableContainer title="Invoice Report">
              <TableToolbar>
                <TableToolbarContent>
                  <TableToolbarSearch
                    expanded
                    onChange={(e) =>
                      setSearchTerm(
                        e.target.value
                      )
                    }
                    placeholder="Search by invoice number, billing name, email,  or status..."
                    value={searchTerm}
                  />
                </TableToolbarContent>
              </TableToolbar>

              {/* Loading */}

              {loading && (
                <div
                  style={{
                    padding: "2rem",
                    textAlign: "center",
                  }}
                >
                  <InlineLoading
                    description="Loading invoice report..."
                  />
                </div>
              )}

              {/* Error */}

              {error &&
                !loading && (
                  <div
                    style={{
                      padding: "1rem",
                    }}
                  >
                    <InlineNotification
                      kind="error"
                      title="Error"
                      subtitle={error}
                      lowContrast
                      onClose={() =>
                        setError("")
                      }
                    />
                  </div>
                )}

              {/* Success */}

              {success && (
                <div
                  style={{
                    padding: "1rem",
                  }}
                >
                  <InlineNotification
                    kind="success"
                    title="Success"
                    subtitle={
                      success
                    }
                    lowContrast
                    onClose={() =>
                      setSuccess("")
                    }
                  />
                </div>
              )}

              <Table
                {...getTableProps()}
              >
                <TableHead>
                  <TableRow>
                    {headers.map(
                      (header) => {
                        const headerProps =
                          getHeaderProps({
                            header,
                          });

                        const {
                          key:
                            headerKey,
                          ...restHeaderProps
                        } = headerProps;

                        return (
                          <TableHeader
                            key={
                              header.key
                            }
                            {...restHeaderProps}
                          >
                            {
                              header.header
                            }
                          </TableHeader>
                        );
                      }
                    )}
                  </TableRow>
                </TableHead>

                <TableBody>
                  {!loading &&
                  paginatedRows.length ===
                    0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={
                          headers.length
                        }
                        style={{
                          textAlign:
                            "center",
                          padding:
                            "2rem",
                        }}
                      >
                        {activeFilterCount >
                          0 ||
                        searchTerm
                          ? "No results found. Try adjusting your search or filters."
                          : "No invoice data found."}
                      </TableCell>
                    </TableRow>
                  ) : (
                    paginatedRows.map(
                      (row) => {
                        const rowProps =
                          getRowProps({
                            row,
                          });

                        const {
                          key: rowKey,
                          ...restRowProps
                        } = rowProps;

                        return (
                          <TableRow
                            key={row.id}
                            {...restRowProps}
                          >
                            {/* Invoice Number */}

                            <TableCell>
                              {row.invoiceNumber ||
                                "N/A"}
                            </TableCell>

                            {/* Created At */}

                            <TableCell>
                              {formatDisplayDate(
                                row.createdAt
                              )}
                            </TableCell>

                            {/* Billing Name */}

                            <TableCell>
                              {row.billingName ||
                                "N/P"}
                            </TableCell>

                            {/* Billing Email */}

                            <TableCell>
                              {row.billingEmail ||
                                "N/P"}
                            </TableCell>

                            {/* Transaction ID 

                            <TableCell>
                              {row.transactionId ||
                                "N/P"}
                            </TableCell> */}

                            {/* Total Amount */}

                            <TableCell>
                              {formatCurrency(
                                row.totalAmount,
                                row.currency
                              )}
                            </TableCell>

                            {/* Status */}

                            <TableCell>
                              <Tag
                                type={getValidTagType(
                                  getStatusTagType(
                                    row.status
                                  )
                                )}
                              >
                                {row.status ||
                                  "N/P"}
                              </Tag>
                            </TableCell>

                            {/* PDF */}

                            <TableCell>
                              {row.pdfPath ? (
                                <Button
                                  kind="ghost"
                                  size="sm"
                                  renderIcon={
                                    Document
                                  }
                                  onClick={() =>
                                    handleViewPdf(
                                      row
                                    )
                                  }
                                >
                                  View PDF
                                </Button>
                              ) : (
                                <span
                                  style={{
                                    color:
                                      "#8d8d8d",
                                  }}
                                >
                                  N/A
                                </span>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      }
                    )
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          );
        }}
      </DataTable>

      {/* ==================================================
          Pagination
      ================================================== */}

      {filteredRows.length >
        0 &&
        !loading && (
          <div
            style={{
              padding:
                "1rem 1.25rem",
              display: "flex",
              justifyContent:
                "flex-end",
            }}
          >
            <Pagination
              totalItems={
                filteredRows.length
              }
              page={page}
              pageSize={pageSize}
              pageSizes={[
                100,
                50,
                20,
                10,
                5,
              ]}
              onChange={({
                page: p,
                pageSize: ps,
              }) => {
                setPage(p);
                setPageSize(ps);
              }}
            />
          </div>
        )}
    </>
  );
}